/**
 * Normaliza texto para busca: sem acento, sem caixa e sem pontuação. É o que faz
 * "ctrl+alt+e", "Ctrl Alt E" e "ctrlalte" acharem o mesmo atalho, e "follow up" achar
 * "Enviar Follow Up".
 *
 * @param {string} texto
 * @returns {string}
 */
export function normalizarBusca(texto) {
  if (typeof texto !== 'string') {
    return ''
  }

  return texto
    .normalize('NFD')
    // Marcas de acentuação, que o NFD separou da letra
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Quebra "Ctrl + Alt + E" nas teclas que serão desenhadas uma por uma.
 *
 * @param {string} combinacao
 * @returns {string[]}
 */
export function dividirCombinacao(combinacao) {
  if (typeof combinacao !== 'string') {
    return []
  }

  return combinacao
    .split('+')
    .map((tecla) => tecla.trim())
    .filter(Boolean)
}

// Separador entre os campos do texto de busca: impede que um termo case atravessando a
// fronteira entre a descrição de um atalho e o nome do grupo.
const SEPARADOR = ' '

function textoDeBusca(atalho) {
  return [atalho?.keyCombo, atalho?.description, atalho?.group].map(normalizarBusca).join(SEPARADOR)
}

/**
 * Um atalho casa a busca quando o termo aparece nas teclas, na descrição ou no grupo.
 *
 * @param {object} atalho
 * @param {string} termo
 * @returns {boolean}
 */
export function atalhoCasaBusca(atalho, termo) {
  const procurado = normalizarBusca(termo)

  if (!procurado) {
    return true
  }

  return textoDeBusca(atalho).includes(procurado)
}

/**
 * Monta os grupos que o modal desenha, na ordem em que foram registrados.
 *
 * Faz três coisas que a lista crua não faz: tira grupos repetidos (cinco telas registram
 * um grupo chamado "Processo"), tira atalhos repetidos dentro do mesmo grupo (registro
 * duplicado ao entrar e sair da tela) e descarta grupo que ficou sem nenhum atalho — sem
 * isso a busca deixaria títulos órfãos na tela.
 *
 * Atalho de um grupo que ninguém registrou continua aparecendo, com o próprio nome como
 * título: melhor um grupo sem descrição do que um atalho invisível.
 *
 * @param {object[]} atalhos
 * @param {object[]} grupos
 * @param {string} termo Busca; vazio devolve tudo
 * @returns {{title: string, description: string, atalhos: object[]}[]}
 */
export function agruparAtalhos(atalhos, grupos, termo = '') {
  const listaDeAtalhos = Array.isArray(atalhos) ? atalhos : []
  const listaDeGrupos = Array.isArray(grupos) ? grupos : []

  const porTitulo = new Map()

  listaDeGrupos.forEach((grupo) => {
    const titulo = grupo?.title

    if (typeof titulo !== 'string' || porTitulo.has(titulo)) {
      return
    }

    porTitulo.set(titulo, { title: titulo, description: grupo.description ?? '', atalhos: [] })
  })

  const vistos = new Set()

  listaDeAtalhos.forEach((atalho) => {
    if (!atalho?.keyCombo || !atalhoCasaBusca(atalho, termo)) {
      return
    }

    const titulo = typeof atalho.group === 'string' ? atalho.group : ''
    const chave = `${titulo}${SEPARADOR}${atalho.keyCombo}`

    if (vistos.has(chave)) {
      return
    }

    vistos.add(chave)

    if (!porTitulo.has(titulo)) {
      porTitulo.set(titulo, { title: titulo, description: '', atalhos: [] })
    }

    porTitulo.get(titulo).atalhos.push(atalho)
  })

  return [...porTitulo.values()].filter((grupo) => grupo.atalhos.length > 0)
}

/**
 * Quantos atalhos existem depois de tirar as repetições. É o total que o contador mostra.
 *
 * @param {object[]} atalhos
 * @param {object[]} grupos
 * @param {string} termo
 * @returns {number}
 */
export function contarAtalhos(atalhos, grupos, termo = '') {
  return agruparAtalhos(atalhos, grupos, termo).reduce(
    (total, grupo) => total + grupo.atalhos.length,
    0
  )
}

/**
 * Texto do contador. Sem busca diz só o total; com busca diz quantos sobraram de quantos,
 * para ninguém achar que a lista encolheu sozinha.
 *
 * @param {number} encontrados
 * @param {number} total
 * @param {string} termo
 * @returns {string}
 */
export function textoDeContagem(encontrados, total, termo = '') {
  const plural = (quantidade) => (quantidade === 1 ? '1 atalho' : `${quantidade} atalhos`)

  if (!normalizarBusca(termo)) {
    return plural(total)
  }

  return `${encontrados} de ${plural(total)}`
}
